import cn from "classnames";
import Card from "../../../../../components/Card";
import { DEFAULT_DECK_AVATAR } from "../../../../../constants/images";
import styles from "./TopicPreview.module.sass";

const TopicPreview = ({
                          visible,
                          onClose,
                          title,
                          description,
                          textColor,
                          backgroundColor,
                          imageUrl,
                          selectedDecks,
                          selectedTexts,
                          startDate,
                          endDate,
                          active,
                          translations
                      }) => {
    // Formatear rango de fechas
    const formatDateRange = () => {
        if (!startDate || !endDate) return "";
        const start = startDate?.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short'
        });
        const end = endDate?.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short'
        });
        return `${start} - ${end}`;
    };

    // Obtener URL de imagen o usar placeholder
    const getImageSrc = () => {
        if (imageUrl) return imageUrl;
        // Usar imagen por defecto si hay título
        if (title) {
            return DEFAULT_DECK_AVATAR;
        }
        return "";
    };

    // Calcular total de palabras
    const calculateTotalWords = () => {
        const deckWords = selectedDecks.reduce((total, deck) => {
            const cards = typeof deck === 'object' ? (deck.totalCards || 0) : 0;
            return total + (cards * 3); // Asumiendo 3 palabras por carta
        }, 0);
        const textWords = selectedTexts.reduce((total, text) => {
            const words = typeof text === 'object' ? (text.wordCount || 0) : 0;
            return total + words;
        }, 0);
        return deckWords + textWords;
    };

    // Helper para extraer título de cualquier item (catalog devuelve title directo)
    const getItemTitle = (obj) => {
        if (!obj || typeof obj !== 'object') return null;
        if (typeof obj.title === 'string' && obj.title.trim()) return obj.title;
        if (typeof obj.titleTrans === 'string' && obj.titleTrans.trim()) return obj.titleTrans;
        if (typeof obj.label === 'string' && obj.label.trim()) return obj.label;
        if (typeof obj.name === 'string' && obj.name.trim()) return obj.name;
        if (Array.isArray(obj.title) && obj.title.length > 0) {
            const first = obj.title[0];
            if (first?.trans && typeof first.trans === 'string') return first.trans;
        }
        return null;
    };

    // Obtener todos los items combinados (decks + texts) con imagen y título reales
    const getContentItems = () => {
        const allItems = [];

        selectedDecks.forEach(deck => {
            allItems.push({
                type: 'deck',
                title: getItemTitle(deck) || 'Deck',
                imageUrl: deck?.imageUrl || null,
                count: typeof deck === 'object' ? (deck.totalCards || deck.contentCount || 100) : 100,
                progress: 0
            });
        });

        selectedTexts.forEach(text => {
            allItems.push({
                type: 'text',
                title: getItemTitle(text) || 'Text',
                imageUrl: text?.imageUrl || null,
                count: typeof text === 'object' ? (text.wordCount || text.contentCount || 50) : 50,
                progress: 0
            });
        });

        return allItems;
    };

    const contentItems = getContentItems();
    const totalWords = calculateTotalWords();
    const hasContent = title || selectedDecks.length > 0 || selectedTexts.length > 0;

    // Si no hay contenido, mostrar preview vacío
    if (!hasContent) {
        return (
            <Card className={cn(styles.card)}>
                <div className={styles.emptyPreview}>
                    <p>Start filling the form to see the preview</p>
                </div>
            </Card>
        );
    }

    return (
        <Card className={cn(styles.card)}>
            <div className={styles.previewContainer}>
                {/* Main Hero Card */}
                <div
                    className={styles.heroCard}
                    style={{
                        backgroundColor: backgroundColor || "#1c1e22",
                    }}
                >
                    {/* Hero Content */}
                    <div className={styles.heroContent}>
                        <div className={styles.heroLeft}>
                            {title && (
                                <h1 className={styles.heroTitle} style={{ color: textColor || "#ffffff" }}>
                                    {title}
                                </h1>
                            )}
                            {description ? (
                                <p className={styles.heroDescription} style={{ color: textColor || "#ffffff" }}>
                                    {description}
                                </p>
                            ) : totalWords > 0 && title && (
                                <p className={styles.heroDescription} style={{ color: textColor || "#ffffff" }}>
                                    Learn +{totalWords} words about {title.toLowerCase()}
                                </p>
                            )}
                        </div>

                        {/* Hero Image - máximo 50% ancho */}
                        {getImageSrc() && (
                            <div className={styles.heroImageContainer}>
                                <img
                                    src={getImageSrc()}
                                    alt={title || "Topic"}
                                    className={styles.heroImage}
                                />
                            </div>
                        )}
                    </div>

                    {/* Content Cards Row */}
                    {contentItems.length > 0 && (
                        <div className={styles.contentRow}>
                            {contentItems.map((item, index) => (
                                <div key={index} className={styles.contentCard}>
                                    <div className={styles.cardHeader}>
                                        <div className={styles.cardThumb}>
                                            <div className={styles.cardIconWrapper}>
                                                <span className={styles.cardIcon}>📚</span>
                                            </div>
                                            {item.imageUrl && (
                                                <img
                                                    src={item.imageUrl}
                                                    alt={item.title}
                                                    className={styles.cardThumbImg}
                                                    onError={({ currentTarget }) => {
                                                        currentTarget.onerror = null;
                                                        currentTarget.style.display = 'none';
                                                    }}
                                                />
                                            )}
                                        </div>
                                        <span className={styles.cardNumber}>{item.count}</span>
                                        {item.progress > 0 && (
                                            <div className={styles.progressContainer}>
                                                <div className={styles.progressBar}>
                                                    <div
                                                        className={styles.progressFill}
                                                        style={{width: `${item.progress}%`}}
                                                    />
                                                </div>
                                                <span className={styles.progressValue}>{item.progress}%</span>
                                            </div>
                                        )}
                                    </div>
                                    <h3 className={styles.cardTitle}>{item.title}</h3>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Stats Section */}
                {(selectedDecks.length > 0 || selectedTexts.length > 0 || startDate || endDate) && (
                    <div className={styles.statsSection}>
                        <div className={styles.statsGrid}>
                            <div className={styles.statItem}>
                                <div className={styles.statValue}>{selectedDecks.length}</div>
                                <div className={styles.statLabel}>Decks</div>
                            </div>
                            <div className={styles.statItem}>
                                <div className={styles.statValue}>{selectedTexts.length}</div>
                                <div className={styles.statLabel}>Texts</div>
                            </div>
                            <div className={styles.statItem}>
                                <div className={styles.statValue}>
                                    {active ? "Active" : "Inactive"}
                                </div>
                                <div className={styles.statLabel}>Status</div>
                            </div>
                        </div>

                        {/* Active Period */}
                        {formatDateRange() && (
                            <div className={styles.periodSection}>
                                <div className={styles.periodLabel}>Active Period:</div>
                                <div className={styles.periodValue}>{formatDateRange()}</div>
                            </div>
                        )}

                        {/* Color Previews */}
                        <div className={styles.colorSection}>
                            <div className={styles.colorItem}>
                                <div
                                    className={styles.colorBlock}
                                    style={{ backgroundColor: textColor || "#ffffff" }}
                                />
                                <span className={styles.colorLabel}>Text</span>
                            </div>
                            <div className={styles.colorItem}>
                                <div
                                    className={styles.colorBlock}
                                    style={{ backgroundColor: backgroundColor || "#1c1e22" }}
                                />
                                <span className={styles.colorLabel}>Background</span>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Card>
    );
};

export default TopicPreview;